import api from '@src/api/clients';

const BASE_URL = 'dashboard/invoices/';

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  );

export const listInvoices = (params = {}, options = {}) =>
  api.request({ url: BASE_URL, method: 'GET', payload: cleanParams(params), options });

export const retrieveInvoice = (invoiceId, params = {}) =>
  api.request({ url: `${BASE_URL}${invoiceId}/`, method: 'GET', payload: cleanParams(params) });

export const getClientChannelBreakdown = (params = {}) =>
  api.request({
    url: `${BASE_URL}client-channel-breakdown/`,
    method: 'GET',
    payload: cleanParams(params),
  });

export const getParentChannelBreakdown = (params = {}) =>
  api.request({
    url: `${BASE_URL}parent-channel-breakdown/`,
    method: 'GET',
    payload: cleanParams(params),
  });

export const getClientSpendPreview = (params = {}) =>
  api.request({
    url: `${BASE_URL}client-spend-preview/`,
    method: 'GET',
    payload: cleanParams(params),
  });

export const listPeriodSummary = (params = {}) =>
  api.request({
    url: `${BASE_URL}period-summary/`,
    method: 'GET',
    payload: cleanParams(params),
  });

export const listInvoiceParentCompanies = () =>
  api.request({
    url: `${BASE_URL}parent-companies/`,
    method: 'GET',
  });

export const listBulkGenerationClients = (params = {}) =>
  api.request({
    url: `${BASE_URL}bulk-generation-clients/`,
    method: 'GET',
    payload: cleanParams(params),
  });

export const retrievePublicInvoice = (params = {}) =>
  api.request({
    url: `${BASE_URL}public/`,
    method: 'GET',
    payload: cleanParams(params),
    auth: false,
  });

export const retrievePublicPaymentIntent = (params = {}) =>
  api.request({
    url: `${BASE_URL}public-payment-intent/`,
    method: 'GET',
    payload: cleanParams(params),
    auth: false,
  });

export const retrievePublicPaymentConfig = (params = {}) =>
  api.request({
    url: `${BASE_URL}public-payment-config/`,
    method: 'GET',
    payload: cleanParams(params),
    auth: false,
  });

export const createPublicAutopayConsent = (payload = {}) =>
  api.request({
    url: `${BASE_URL}public-autopay-consent/`,
    method: 'POST',
    payload: cleanParams(payload),
    auth: false,
  });

export const reportPublicPayment = (payload = {}) =>
  api.request({
    url: `${BASE_URL}public-report-payment/`,
    method: 'POST',
    payload,
  });

export const retrievePublicPaymentStatus = (params = {}) =>
  api.request({
    url: `${BASE_URL}public-payment-status/`,
    method: 'GET',
    payload: cleanParams(params),
    auth: false,
  });

export const downloadPublicInvoicePdf = ({ number, token }) =>
  api
    .request({
      url: `${BASE_URL}public-pdf/`,
      method: 'GET',
      payload: cleanParams({ number, token }),
      auth: false,
      options: { responseType: 'blob' },
    })
    .then((response) => response.data);

export const createInvoice = (payload) =>
  api.request({ url: BASE_URL, method: 'POST', payload });

export const updateInvoice = (invoiceId, payload) =>
  api.request({ url: `${BASE_URL}${invoiceId}/`, method: 'PATCH', payload });

export const deleteInvoice = (invoiceId) =>
  api.request({ url: `${BASE_URL}${invoiceId}/`, method: 'DELETE' });

export const downloadInvoicePdf = (invoiceId) =>
  api
    .request({
      url: `${BASE_URL}${invoiceId}/pdf/`,
      method: 'GET',
      options: { responseType: 'blob' },
    })
    .then((response) => response.data);

export const sendInvoice = (invoiceId, payload = {}) =>
  api.request({ url: `${BASE_URL}${invoiceId}/send/`, method: 'POST', payload });

export const markInvoicePaid = (invoiceId, payload = {}) =>
  api.request({ url: `${BASE_URL}${invoiceId}/mark-paid/`, method: 'POST', payload });

export const resendInvoiceReceipt = (invoiceId, payload = {}) =>
  api.request({ url: `${BASE_URL}${invoiceId}/resend-receipt/`, method: 'POST', payload });

export const reportInvoicePayment = (invoiceId, payload = {}) =>
  api.request({ url: `${BASE_URL}${invoiceId}/report-payment/`, method: 'POST', payload });

export const clearInvoicePaymentReport = (invoiceId) =>
  api.request({ url: `${BASE_URL}${invoiceId}/clear-payment-report/`, method: 'POST' });

export const recordPayment = (invoiceId, payload = {}) =>
  api.request({ url: `${BASE_URL}${invoiceId}/record-payment/`, method: 'POST', payload });

export const autoChargeInvoice = (invoiceId, payload = {}) =>
  api.request({ url: `${BASE_URL}${invoiceId}/auto-charge/`, method: 'POST', payload });

export const listPayments = (invoiceId) =>
  api.request({ url: `${BASE_URL}${invoiceId}/payments/`, method: 'GET' });

export const waiveLateStrike = (invoiceId, payload = {}) =>
  api.request({ url: `${BASE_URL}${invoiceId}/waive-late-strike/`, method: 'POST', payload });

export const getLatePaymentStandings = (params = {}) =>
  api.request({ url: `${BASE_URL}late-payment-standings/`, method: 'GET', payload: cleanParams(params) });

export const voidInvoice = (invoiceId, payload = {}) =>
  api.request({ url: `${BASE_URL}${invoiceId}/void/`, method: 'POST', payload });

export const bulkGenerateInvoices = (payload) =>
  api.request({ url: `${BASE_URL}bulk-generate/`, method: 'POST', payload });

export const bulkSendInvoices = (payload) =>
  api.request({ url: `${BASE_URL}bulk-send/`, method: 'POST', payload });

export const bulkVoidInvoices = (payload) =>
  api.request({ url: `${BASE_URL}bulk-void/`, method: 'POST', payload });

export const uploadInvoiceAttachment = (invoiceId, formData) =>
  api.request({
    url: `${BASE_URL}${invoiceId}/attachments/`,
    method: 'POST',
    payload: formData,
    multipart: true,
  });

export const retrieveInvoiceClientSettings = (clientId) =>
  api.request({ url: `dashboard/clients/${clientId}/`, method: 'GET' });

export const updateInvoiceClientSettings = (clientId, payload) =>
  api.request({ url: `dashboard/clients/${clientId}/`, method: 'PATCH', payload });

export const listInvoiceBillingContacts = (clientId, params = {}) =>
  api.request({
    url: 'dashboard/client-pocs/',
    method: 'GET',
    payload: cleanParams({ client_id: clientId, ...params }),
  });

export const createInvoiceBillingContact = (payload) =>
  api.request({ url: 'dashboard/client-pocs/', method: 'POST', payload });

export const updateInvoiceBillingContact = (contactId, payload) =>
  api.request({ url: `dashboard/client-pocs/${contactId}/`, method: 'PATCH', payload });

export const deleteInvoiceBillingContact = (contactId) =>
  api.request({ url: `dashboard/client-pocs/${contactId}/`, method: 'DELETE' });

// ---------------------------------------------------------------------------
// Client-member authenticated invoice endpoints
// ---------------------------------------------------------------------------

const MY_INVOICES_URL = 'dashboard/my-invoices/';

export const listMyInvoices = (params = {}, options = {}) =>
  api.request({ url: MY_INVOICES_URL, method: 'GET', payload: cleanParams(params), options });

export const retrieveMyInvoice = (number, params = {}) =>
  api.request({ url: `${MY_INVOICES_URL}${number}/`, method: 'GET', payload: cleanParams(params) });

export const downloadMyInvoicePdf = (number) =>
  api
    .request({
      url: `${MY_INVOICES_URL}${number}/pdf/`,
      method: 'GET',
      options: { responseType: 'blob' },
    })
    .then((res) => res.data);

export const retrieveMyInvoicePaymentConfig = (number, params = {}) =>
  api.request({
    url: `${MY_INVOICES_URL}${number}/payment-config/`,
    method: 'GET',
    payload: cleanParams(params),
  });

export const retrieveMyInvoicePaymentIntent = (number, params = {}) =>
  api.request({
    url: `${MY_INVOICES_URL}${number}/payment-intent/`,
    method: 'GET',
    payload: cleanParams(params),
  });

export const retrieveMyInvoicePaymentStatus = (number) =>
  api.request({ url: `${MY_INVOICES_URL}${number}/payment-status/`, method: 'GET' });

export const retrieveMyPaymentStanding = (params = {}) =>
  api.request({ url: `${MY_INVOICES_URL}payment-standing/`, method: 'GET', payload: cleanParams(params) });

export const createMyInvoiceAutopayConsent = (number, payload = {}) =>
  api.request({
    url: `${MY_INVOICES_URL}${number}/autopay-consent/`,
    method: 'POST',
    payload: cleanParams(payload),
  });

export default {
  listInvoices,
  listPeriodSummary,
  retrieveInvoice,
  getClientChannelBreakdown,
  getParentChannelBreakdown,
  getClientSpendPreview,
  listBulkGenerationClients,
  retrievePublicInvoice,
  retrievePublicPaymentConfig,
  createPublicAutopayConsent,
  retrievePublicPaymentIntent,
  retrievePublicPaymentStatus,
  downloadPublicInvoicePdf,
  downloadInvoicePdf,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  sendInvoice,
  markInvoicePaid,
  recordPayment,
  autoChargeInvoice,
  listPayments,
  waiveLateStrike,
  getLatePaymentStandings,
  voidInvoice,
  bulkGenerateInvoices,
  bulkSendInvoices,
  bulkVoidInvoices,
  uploadInvoiceAttachment,
  retrieveInvoiceClientSettings,
  updateInvoiceClientSettings,
  listInvoiceBillingContacts,
  createInvoiceBillingContact,
  updateInvoiceBillingContact,
  deleteInvoiceBillingContact,
  listMyInvoices,
  retrieveMyInvoice,
  downloadMyInvoicePdf,
  retrieveMyInvoicePaymentConfig,
  retrieveMyInvoicePaymentIntent,
  retrieveMyInvoicePaymentStatus,
  retrieveMyPaymentStanding,
  createMyInvoiceAutopayConsent,
};
